/* 0.3s Release inline purchase controller — Figma 626:2161 */
(() => {
  "use strict";
  if (customElements.get("nex-hero-purchase")) return;

  class NexHeroPurchase extends HTMLElement {
    connectedCallback() {
      if (this.__heroPurchaseInit) return;
      this.__heroPurchaseInit = true;
      this.cards = [...this.querySelectorAll(".hero-purchase__color")];
      this.checkoutForm = this.querySelector(".hero-purchase__checkout-form");
      this.checkoutButton = this.querySelector(".hero-purchase__checkout");
      this.variantInput = this.querySelector("[data-purchase-variant]");
      this.error = this.querySelector(".hero-purchase__error");
      this.hero = this.closest(".hero");
      this.actions = this.querySelector(".hero-purchase__actions");
      this.buyShell = this.querySelector(".hero-purchase__buy");
      this.productImage = this.querySelector("[data-purchase-product-image]");
      this.reviewProof = this.querySelector("[data-hero-review-proof]");
      this.reviewCopy = this.querySelector("[data-hero-review-copy]");
      this._cleanup = [];
      const on = (target, event, handler, options) => {
        target.addEventListener(event, handler, options);
        this._cleanup.push(() => target.removeEventListener(event, handler, options));
      };
      on(window, "pagehide", () => this.resetCheckout());
      on(window, "pageshow", (event) => {
        if (event.persisted || this._checkoutAttempt?.navigating) this.resetCheckout();
      });

      this.cards.forEach((card) => on(card, "click", () => {
        if (card.disabled || card.getAttribute("aria-disabled") === "true") return;
        this.select(card, { announce: true, syncDock: true, animate: true });
      }));
      if (this.checkoutForm) on(this.checkoutForm, "submit", (event) => this.checkout(event));
      on(document, "gdab:color-select", (event) => {
        const id = String(event.detail && event.detail.variantId || "");
        const match = this.cards.find((card) => card.dataset.variantId === id);
        if (match) this.select(match, { announce: false, syncDock: false, animate: true });
      });

      this.armShopPayGuard();
      const initial = this.cards.find((card) =>
        card.getAttribute("aria-checked") === "true" && !card.disabled)
        || this.cards.find((card) => !card.disabled);
      if (initial) this.select(initial, { announce: false, syncDock: false, animate: false });
      this.mobileMedia = matchMedia("(max-width: 1023.98px)");
      on(this.mobileMedia, "change", () => this.syncReviewMode());
      on(document, "visibilitychange", () => this.syncReviewMode());
      this.syncReviewMode();
    }

    disconnectedCallback() {
      this.resetCheckout();
      (this._cleanup || []).forEach((dispose) => dispose());
      this._cleanup = [];
      clearTimeout(this._changeTimer);
      clearTimeout(this._bagTimer);
      clearTimeout(this._reviewTimer);
      clearTimeout(this._reviewSwapTimer);
      clearTimeout(this._reviewGlowTimer);
      if (this._reviewLayoutRaf) cancelAnimationFrame(this._reviewLayoutRaf);
      this._reviewLayoutRaf = 0;
      if (this._reviewResizeObserver) this._reviewResizeObserver.disconnect();
      this._reviewResizeObserver = null;
      if (this._reviewMeasure) this._reviewMeasure.remove();
      this._reviewMeasure = null;
      if (this._reviewAbort) this._reviewAbort.abort();
      this._reviewAbort = null;
      this._reviewLoadPromise = null;
      this._reviewsInitialized = false;
      this.mobileMedia = null;
      if (this.reviewCopy) {
        this.reviewCopy.classList.remove("is-leaving", "is-entering");
      }
      if (this._bagShell) this._bagShell.classList.remove("is-color-changing");
      if (this._bagShell && this._bagAnimationEnd) {
        this._bagShell.removeEventListener("animationend", this._bagAnimationEnd);
      }
      this._bagShell = null;
      this._bagAnimationEnd = null;
      if (this._walletRaf) cancelAnimationFrame(this._walletRaf);
      this._walletRaf = 0;
      this.__heroPurchaseInit = false;
    }

    armShopPayGuard() {
      if (!this.actions || !this.buyShell) return;
      this.setShopPayState("pending");
      const observer = new MutationObserver(() => this.queueShopPayCheck());
      observer.observe(this.buyShell, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["recommended", "class", "hidden", "style"],
      });
      this._cleanup.push(() => observer.disconnect());

      [0, 120, 420, 900, 1600].forEach((delay) => {
        const timer = setTimeout(() => this.queueShopPayCheck(), delay);
        this._cleanup.push(() => clearTimeout(timer));
      });
    }

    queueShopPayCheck() {
      if (this._walletRaf) return;
      this._walletRaf = requestAnimationFrame(() => {
        this._walletRaf = 0;
        this.refreshShopPayState();
      });
    }

    refreshShopPayState() {
      if (!this.actions || !this.buyShell) return;
      const active = [...this.querySelectorAll(".hero-purchase__buy-form")]
        .find((form) => !form.hidden);
      if (!active) {
        this.setShopPayState("unavailable");
        return;
      }

      const unbranded = active.querySelector(".shopify-payment-button__button--unbranded");
      const accelerated = active.querySelector("shopify-accelerated-checkout");
      const wallet = active.querySelector("shop-pay-wallet-button");
      let recommendation = null;
      if (accelerated) {
        try {
          recommendation = JSON.parse(accelerated.getAttribute("recommended") || "null");
        } catch (_) {
          recommendation = null;
        }
      }

      if (unbranded || (recommendation && recommendation.name !== "shop_pay")) {
        this.setShopPayState("unavailable");
      } else if (recommendation && recommendation.name === "shop_pay" && wallet) {
        this.setShopPayState("available");
      } else {
        this.setShopPayState("pending");
      }
    }

    setShopPayState(state) {
      if (!this.actions || !this.buyShell) return;
      const available = state === "available";
      this.actions.dataset.shopPay = state;
      this.buyShell.setAttribute("aria-hidden", available ? "false" : "true");
      this.buyShell.inert = !available || Boolean(window.NexDock?.busy || window.NexDock?.preview);
    }

    select(card, options = {}) {
      if (!card || card.disabled) return;
      const changed = !this.selected || this.selected.dataset.variantId !== card.dataset.variantId;
      this.selected = card;
      this.cards.forEach((item) =>
        item.setAttribute("aria-checked", item === card ? "true" : "false"));

      const write = (selector, value) => {
        const node = this.querySelector(selector);
        if (node) node.textContent = value || "";
      };
      write("[data-purchase-mode]", card.dataset.mode);
      write("[data-purchase-price]", card.dataset.price);
      window.NexPreorderPerks?.sync(this, card);
      if (this.checkoutButton && !this._checkoutAttempt && card.dataset.checkoutLabel) {
        this.checkoutButton.textContent = card.dataset.checkoutLabel;
      }
      if (this.variantInput) this.variantInput.value = card.dataset.variantId || "";

      this.setShopPayState("pending");
      this.querySelectorAll(".hero-purchase__buy-form").forEach((form) => {
        const formVariant = form.querySelector('input[name="id"]')?.value || "";
        form.hidden = formVariant !== String(card.dataset.variantId || "");
      });
      this.queueShopPayCheck();
      this.updateBag(
        card.dataset.color,
        card.dataset.productName,
        Boolean(changed && options.animate),
        card.dataset.image,
      );

      if (changed && options.animate && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.classList.remove("is-changing");
        void this.offsetWidth;
        this.classList.add("is-changing");
        clearTimeout(this._changeTimer);
        this._changeTimer = setTimeout(() => this.classList.remove("is-changing"), 380);
      }
      if (this.error) this.error.hidden = true;

      if (options.syncDock && matchMedia("(max-width: 1023.98px)").matches) {
        document.dispatchEvent(new CustomEvent("gdab:select-variant", {
          bubbles: true,
          detail: {
            variantId: card.dataset.variantId,
            productUrl: card.dataset.productUrl,
            productHandle: card.dataset.productHandle,
            color: card.dataset.color,
          },
        }));
      }
      if (options.announce) {
        const label = card.getAttribute("aria-label") || card.dataset.productName || "Color selected";
        this.setAttribute("aria-label", label);
      }
    }

    updateBag(color, productName, animate = false, productImageUrl = "") {
      if (this.productImage && productImageUrl) {
        this.productImage.src = productImageUrl;
        if (productName) this.productImage.alt = productName;
      }
      const desktopView = matchMedia("(min-width: 1024px)").matches;
      if (!this.hero || !color) return;
      this.hero.dataset.productColor = color;
      if (this.classList.contains("hero-purchase--index") && !desktopView) return;
      const suffix = color.charAt(0).toUpperCase() + color.slice(1);
      const mobile = this.dataset["bag" + suffix + "Mobile"];
      const desktop = this.dataset["bag" + suffix + "Desktop"] || mobile;
      const image = this.hero.querySelector(".hero__bag");
      if (!image || !mobile) return;
      const picture = image.closest("picture");
      if (picture) {
        picture.querySelectorAll("source").forEach((source) => {
          source.srcset = (source.media || "").includes("min-width") ? desktop : mobile;
          source.sizes = "";
        });
      }
      image.src = desktopView ? desktop : mobile;
      image.srcset = desktopView ? desktop : mobile;
      if (productName) image.alt = productName;

      const shell = image.closest(".hero__bag-shell");
      clearTimeout(this._bagTimer);
      if (this._bagShell && this._bagAnimationEnd) {
        this._bagShell.removeEventListener("animationend", this._bagAnimationEnd);
      }
      if (this._bagShell) this._bagShell.classList.remove("is-color-changing");
      this._bagShell = shell;
      this._bagAnimationEnd = null;
      if (!shell || !animate ||
          matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      const clear = () => {
        clearTimeout(this._bagTimer);
        shell.classList.remove("is-color-changing");
        shell.removeEventListener("animationend", onEnd);
        if (this._bagShell === shell) {
          this._bagShell = null;
          this._bagAnimationEnd = null;
        }
      };
      const onEnd = (event) => {
        if (event.target !== shell ||
            event.animationName !== "purchase-bag-color-in") return;
        clear();
      };
      this._bagAnimationEnd = onEnd;
      shell.addEventListener("animationend", onEnd);
      shell.classList.remove("is-color-changing");
      void shell.offsetWidth;
      shell.classList.add("is-color-changing");
      this._bagTimer = setTimeout(clear, 460);
    }

    initReviews() {
      if (!this.reviewProof || !this.reviewCopy) return;
      this._reviewsInitialized = true;
      this.reviewIndex = 0;
      this.reviews = [];
      this.setupReviewSizing();
      this.startReviewRotation();
      this.loadReviews();
    }

    syncReviewMode() {
      if (!this.reviewProof || !this.reviewCopy || !this.mobileMedia) return;
      if (this.mobileMedia.matches && document.visibilityState !== "hidden") {
        if (!this._reviewsInitialized) {
          this.initReviews();
        } else {
          this.startReviewRotation();
          if (!this._reviewLoadPromise) this.loadReviews();
        }
        return;
      }
      clearTimeout(this._reviewTimer);
      clearTimeout(this._reviewSwapTimer);
      clearTimeout(this._reviewGlowTimer);
      if (!this.mobileMedia.matches && this._reviewAbort) {
        this._reviewAbort.abort();
        this._reviewAbort = null;
        this._reviewLoadPromise = null;
      }
      this.reviewCopy.classList.remove("is-leaving", "is-entering");
    }

    normalizeReviews(reviews) {
      const seen = new Set();
      const productGroup = String(this.reviewProof?.dataset.reviewProductGroup || "")
        .trim().toLocaleLowerCase();
      return (Array.isArray(reviews) ? reviews : []).reduce((eligible, review) => {
        const body = String(review?.body || "").replace(/\s+/g, " ").trim();
        const group = String(review?.productGroupKey || "").trim().toLocaleLowerCase();
        const timestamp = Date.parse(review?.publishedAt || review?.createdAt || "");
        if (!body || Number(review?.rating) !== 5 || !productGroup ||
            group !== productGroup || review?.metadata?.is_store_review === true ||
            !Number.isFinite(timestamp)) return eligible;
        const key = String(review.id || body).toLocaleLowerCase();
        if (seen.has(key)) return eligible;
        seen.add(key);
        eligible.push({ ...review, body, timestamp });
        return eligible;
      }, []).sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
    }

    setupReviewSizing() {
      if (!this.reviewCopy || this._reviewResizeObserver) return;
      const queue = () => {
        if (!this.isConnected || !this.__heroPurchaseInit) return;
        const width = Math.round(this.reviewCopy.getBoundingClientRect().width * 10) / 10;
        if (width > 0 && width === this._reviewMeasuredWidth) return;
        this._reviewMeasuredWidth = width;
        if (this._reviewLayoutRaf) cancelAnimationFrame(this._reviewLayoutRaf);
        this._reviewLayoutRaf = requestAnimationFrame(() => {
          this._reviewLayoutRaf = 0;
          if (this.mobileMedia && this.mobileMedia.matches &&
              document.visibilityState !== "hidden") this.startReviewRotation();
        });
      };
      if (typeof ResizeObserver === "function") {
        this._reviewResizeObserver = new ResizeObserver(queue);
        this._reviewResizeObserver.observe(this.reviewCopy);
      }
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this._reviewMeasuredWidth = -1;
          queue();
        });
      }
    }

    reviewPool() {
      return this.reviews || [];
    }

    reviewLabel(review) {
      const body = String(review && review.body || "").trim();
      const name = String(review && review.reviewerName || "").trim();
      return body ? `“${body}”${name ? ` — ${name}` : ""}` : "";
    }

    startReviewRotation() {
      clearTimeout(this._reviewTimer);
      clearTimeout(this._reviewSwapTimer);
      clearTimeout(this._reviewGlowTimer);
      if (this.reviewCopy) {
        this.reviewCopy.classList.remove("is-leaving", "is-entering");
      }
      if (!this.reviewCopy) return;
      const reviews = this.reviewPool();
      this.reviewCopy.hidden = !reviews.length;
      if (!reviews.length) {
        this.reviewCopy.textContent = "";
        return;
      }
      const show = () => {
        const review = reviews[this.reviewIndex % reviews.length];
        this.reviewCopy.textContent = this.reviewLabel(review);
        this.reviewCopy.dataset.reviewId = String(review.id || "");
        this.reviewIndex = (this.reviewIndex + 1) % reviews.length;
      };
      show();
      if (
        reviews.length < 2 ||
        document.visibilityState === "hidden" ||
        matchMedia("(prefers-reduced-motion: reduce)").matches
      ) return;
      const next = () => {
        if (
          !this.mobileMedia ||
          !this.mobileMedia.matches ||
          document.visibilityState === "hidden" ||
          matchMedia("(prefers-reduced-motion: reduce)").matches
        ) return;
        this.reviewCopy.classList.remove("is-entering");
        this.reviewCopy.classList.add("is-leaving");
        this._reviewSwapTimer = setTimeout(() => {
          show();
          this.reviewCopy.classList.remove("is-leaving");
          void this.reviewCopy.offsetWidth;
          this.reviewCopy.classList.add("is-entering");
          this._reviewGlowTimer = setTimeout(() => {
            this.reviewCopy.classList.remove("is-entering");
          }, 900);
        }, 260);
        this._reviewTimer = setTimeout(next, 5000);
      };
      this._reviewTimer = setTimeout(next, 5000);
    }

    loadReviews() {
      const endpoint = this.reviewProof && this.reviewProof.dataset.reviewApi;
      if (!endpoint || this._reviewLoadPromise) return this._reviewLoadPromise;
      const controller = new AbortController();
      this._reviewAbort = controller;
      this._reviewLoadPromise = (async () => {
        const reviews = [];
        let cursor = "";
        let pages = 0;
        do {
          const url = new URL(endpoint, window.location.origin);
          if (cursor) url.searchParams.set("cursor", cursor);
          const response = await fetch(url.toString(), {
            credentials: "omit",
            signal: controller.signal,
            headers: { Accept: "application/json" },
          });
          if (!response.ok) throw new Error(`Reviews request failed (${response.status})`);
          const payload = await response.json();
          if (Array.isArray(payload.reviews)) reviews.push(...payload.reviews);
          cursor = String(payload.nextCursor || "");
          pages += 1;
        } while (cursor && this.normalizeReviews(reviews).length < 5 && pages < 20);
        this.reviews = this.normalizeReviews(reviews);
        this.reviewIndex = 0;
        if (
          this.mobileMedia &&
          this.mobileMedia.matches &&
          document.visibilityState !== "hidden"
        ) this.startReviewRotation();
      })().catch((error) => {
        if (error.name !== "AbortError") {
          this.dispatchEvent(new CustomEvent("hero-purchase:reviews-error", { bubbles: true }));
        }
      }).finally(() => {
        if (this._reviewAbort === controller) this._reviewAbort = null;
      });
      return this._reviewLoadPromise;
    }

    resetCheckout() {
      const attempt = this._checkoutAttempt;
      if (!attempt) return;
      this._checkoutAttempt = null;
      attempt.controller.abort();
      this.checkoutButton.disabled = false;
      this.checkoutButton.removeAttribute("aria-busy");
      this.checkoutButton.textContent = this.selected?.dataset.checkoutLabel || attempt.label;
    }

    async checkout(event) {
      event.preventDefault();
      if (!this.selected || !this.checkoutButton || this.checkoutButton.disabled || this._checkoutAttempt) return;
      if (window.NexDock?.handlesPurchase?.(this)) return window.NexDock.checkout(this);
      const id = this.selected.dataset.variantId;
      if (!id) return;
      const attempt = {
        label: this.checkoutButton.textContent,
        controller: new AbortController(),
        navigating: false,
      };
      this._checkoutAttempt = attempt;
      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute("aria-busy", "true");
      this.checkoutButton.textContent = "Opening checkout…";
      if (this.error) this.error.hidden = true;
      try {
        const item = { id: Number(id), quantity: 1 };
        if (this._dockGuardAddedVariant !== String(item.id)) {
          const response = await fetch(this.dataset.addUrl || "/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            signal: attempt.controller.signal,
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });
          if (!response.ok) {
            let message = "This color could not be added. Please try again.";
            try {
              const payload = await response.json();
              message = payload.description || payload.message || message;
            } catch (_) {}
            throw new Error(message);
          }
          this._dockGuardAddedVariant = String(item.id);
        }
        if (this._checkoutAttempt !== attempt || !this.isConnected) return;
        // A Dock retained from an older visit must be removed in the cart first.
        // Keep the successful add across a failed check so Retry cannot add it twice.
        const cartController = new AbortController();
        const cancelCart = () => cartController.abort();
        attempt.controller.signal.addEventListener("abort", cancelCart, { once: true });
        const cartTimeout = setTimeout(cancelCart, 6000);
        let cart;
        try {
          const cartResponse = await fetch(this.dataset.cartUrl || "/cart.js", {
            credentials: "same-origin", cache: "no-store",
            headers: { Accept: "application/json" }, signal: cartController.signal,
          });
          if (!cartResponse.ok) throw new Error("cart-check-failed");
          cart = await cartResponse.json();
          if (!Array.isArray(cart?.items)) throw new Error("cart-check-failed");
        } catch (error) {
          throw new Error(this.dataset.cartCheckError || "We could not confirm your cart. Please try again.");
        } finally {
          clearTimeout(cartTimeout);
          attempt.controller.signal.removeEventListener("abort", cancelCart);
        }
        if (this._checkoutAttempt !== attempt || !this.isConnected) return;
        this._dockGuardAddedVariant = null;
        attempt.navigating = true;
        window.location.assign(cart.items.some(item =>
          (item.handle || item.product?.handle) === "chest-bag-magnetic-dock"
          || (this.dataset.dockVariantId && String(item.variant_id || item.id) === this.dataset.dockVariantId))
          ? (this.dataset.cartPageUrl || "/cart") : (this.dataset.checkoutUrl || "/checkout"));
      } catch (error) {
        if (this._checkoutAttempt !== attempt) return;
        this.resetCheckout();
        if (this.error) {
          this.error.textContent = error && error.message
            ? error.message
            : "This color could not be added. Please try again.";
          this.error.hidden = false;
        }
      }
    }
  }

  customElements.define("nex-hero-purchase", NexHeroPurchase);
})();




(() => {
  'use strict';
  const root = document.documentElement;
  const fitted = '--purchase-bag-fit-height';
  const entries = new Map();
  let frame = 0;
  const number = value => Number.parseFloat(value) || 0;
  const layoutTop = element => {
    let top = 0;
    for (let node = element; node; node = node.offsetParent) top += node.offsetTop;
    return top;
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(measure);
  };
  function measure() {
    frame = 0;
    if (document.hidden || (window.visualViewport && Math.abs(window.visualViewport.scale - 1) > .02)) return;
    const rootStyle = getComputedStyle(root);
    for (const [surface, entry] of entries) {
      if (!surface.isConnected) {
        entry.resize.disconnect();
        entry.mutation.disconnect();
        entries.delete(surface);
        continue;
      }
      surface.style.removeProperty(fitted);
      entry.visual.parentElement.style.removeProperty('--purchase-landscape-shell-height');
      if (!surface.offsetWidth || (entry.hero && innerWidth >= 1024)) continue;
      // A side-by-side or absolutely positioned image cannot reduce the content height.
      const visualStyle = getComputedStyle(entry.visual);
      const shellStyle = getComputedStyle(entry.visual.parentElement);
      if (visualStyle.position === 'absolute' || visualStyle.position === 'fixed') {
        if (entry.hero) {
          const pricing = surface.querySelector('.hero-purchase__pricing');
          const pricingStyle = getComputedStyle(pricing);
          const contentHeight = Math.max(0, ...Array.from(pricing.children, child => child.offsetHeight));
          const shellHeight = Math.max(
            number(visualStyle.top) + entry.visual.offsetHeight + number(visualStyle.bottom),
            number(pricingStyle.top) + contentHeight + number(pricingStyle.bottom)
          );
          entry.visual.parentElement.style.setProperty('--purchase-landscape-shell-height', Math.ceil(shellHeight) + 'px');
        }
        continue;
      }
      if (shellStyle.display === 'grid' && shellStyle.gridTemplateColumns.trim().split(/\s+/).length > 1) continue;
      const natural = entry.visual.offsetHeight;
      if (!natural) continue;
      let available;
      let fixed;
      if (entry.hero) {
        const hero = surface.closest('.hero');
        const height = number(getComputedStyle(hero).getPropertyValue('--hero-viewport-height')) || innerHeight;
        const inset = Math.max(number(rootStyle.getPropertyValue('--nex-hero-safe-bottom')), number(rootStyle.getPropertyValue('--nex-preview-bottom-inset')));
        available = height - inset - 12;
        fixed = layoutTop(entry.actions) - layoutTop(hero) + entry.actions.offsetHeight - natural;
      } else {
        available = number(getComputedStyle(surface).maxHeight) - 2;
        fixed = surface.scrollHeight - natural;
      }
      const minimum = entry.hero ? 96 : 80;
      const height = Math.floor(Math.min(natural, Math.max(minimum, available - fixed)));
      const value = height + 'px';
      surface.style.setProperty(fitted, value);
    }
    // The guarantee band follows the purchase content, not an unused viewport
    // floor. Keep the separate animation story height and desktop layout intact.
    for (const [surface, entry] of entries) {
      if (!entry.hero) continue;
      const hero = surface.closest('.hero[data-highlights-separated]');
      if (!hero) continue;
      if (innerWidth >= 1024 || !surface.offsetWidth) {
        hero.style.removeProperty('--hero-flow-stage-height');
        continue;
      }
      const end = Math.ceil(layoutTop(surface) - layoutTop(hero) + surface.offsetHeight);
      const value = end + 'px';
      if (end > 0 && hero.style.getPropertyValue('--hero-flow-stage-height') !== value) {
        hero.style.setProperty('--hero-flow-stage-height', value);
      }
    }
  }
  function connect() {
    document.querySelectorAll('nex-hero-purchase.hero-purchase--index, nex-gdab .gdab__panel').forEach(surface => {
      if (entries.has(surface)) return;
      const hero = surface.matches('nex-hero-purchase');
      const visual = surface.querySelector(hero ? '.hero-purchase__product-visual' : '.gdab__product-visual');
      const actions = surface.querySelector(hero ? '.hero-purchase__actions' : '.gdab__confirm-row');
      if (!visual || !actions) return;
      const resize = new ResizeObserver(schedule);
      resize.observe(surface);
      surface.querySelectorAll('.hero-purchase__pricing, .gdab__purchase-pricing, .hero-purchase__promises, .gdab__promises').forEach(node => resize.observe(node));
      const mutation = new MutationObserver(schedule);
      mutation.observe(surface, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'data-shop-pay', 'data-dock-state'] });
      entries.set(surface, { hero, visual, actions, resize, mutation });
    });
    measure();
  }
  new MutationObserver(schedule).observe(root, { attributes: true, attributeFilter: ['style', 'data-nex-hero-height'] });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', schedule);
  ['gdab:panel-open', 'gdab:color-select', 'gdab:dynamic-text'].forEach(event => document.addEventListener(event, schedule));
  document.addEventListener('shopify:section:load', connect);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connect, { once: true });
  else connect();
  document.fonts?.ready.then(schedule);
})();
